<template>
  <div
    v-if="show"
    class="email-composer-overlay"
    :class="{ 'dragging-active': !isInitialPosition }"
  >
    <div
      ref="modalRef"
      class="email-composer"
      :class="{ 'is-dragging': isDragging }"
      :style="modalStyle"
    >
      <div
        class="email-composer__header email-composer__header--draggable"
        @mousedown="startDrag"
        @touchstart="startDrag"
      >
        <div class="email-composer__header-left">
          <span class="email-composer__header-title">{{
            isReply ? "Reply" : isForward ? "Forward" : "New Message"
          }}</span>
        </div>
        <div class="email-composer__header-right">
          <button
            type="button"
            class="email-composer__header-btn"
            @click="handleClose"
            title="Close"
          >
            <Icon icon="mdi:close" />
          </button>
        </div>
      </div>

      <div class="email-composer__content">
        <form @submit.prevent="handleSend">
          <!-- From -->
          <div v-if="accounts && accounts.length > 0" class="email-composer__row">
            <div class="email-composer__row-label">
              <span>From</span>
              <span class="email-composer__required">*</span>
            </div>
            <div class="email-composer__row-input">
              <Select v-model="formData.account_id" :options="accountOptions" size="sm" required />
            </div>
          </div>

          <!-- To -->
          <div class="email-composer__row">
            <div class="email-composer__row-label">
              <span>To</span>
              <span class="email-composer__required">*</span>
            </div>
            <div class="email-composer__row-input">
              <div class="email-composer__recipients">
                <div
                  v-for="(recipient, index) in formData.to"
                  :key="index"
                  class="email-composer__recipient-tag"
                >
                  <span>{{ recipient.name || recipient.email }}</span>
                  <button
                    type="button"
                    class="email-composer__recipient-remove"
                    @click="removeRecipient('to', index)"
                  >
                    <Icon icon="mdi:close" />
                  </button>
                </div>
                <div class="email-composer__recipient-input-wrapper">
                  <input
                    v-model="recipientInput"
                    type="text"
                    class="email-composer__recipient-input"
                    :placeholder="formData.to.length > 0 ? '' : 'Recipients'"
                    @keydown.enter.prevent="addRecipient('to', recipientInput)"
                    @blur="addRecipient('to', recipientInput)"
                  />
                  <button
                    v-if="!showCcBcc"
                    type="button"
                    class="email-composer__cc-toggle"
                    @click="showCcBcc = true"
                  >
                    Cc Bcc
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- CC -->
          <div v-if="showCcBcc" class="email-composer__row">
            <div class="email-composer__row-label">Cc</div>
            <div class="email-composer__row-input">
              <div class="email-composer__recipients">
                <div
                  v-for="(recipient, index) in formData.cc"
                  :key="index"
                  class="email-composer__recipient-tag"
                >
                  <span>{{ recipient.name || recipient.email }}</span>
                  <button
                    type="button"
                    class="email-composer__recipient-remove"
                    @click="removeRecipient('cc', index)"
                  >
                    <Icon icon="mdi:close" />
                  </button>
                </div>
                <input
                  v-model="ccInput"
                  type="text"
                  class="email-composer__recipient-input"
                  placeholder="Cc"
                  @keydown.enter.prevent="addRecipient('cc', ccInput)"
                  @blur="addRecipient('cc', ccInput)"
                />
              </div>
            </div>
          </div>

          <!-- BCC -->
          <div v-if="showCcBcc" class="email-composer__row">
            <div class="email-composer__row-label">Bcc</div>
            <div class="email-composer__row-input">
              <div class="email-composer__recipients">
                <div
                  v-for="(recipient, index) in formData.bcc"
                  :key="index"
                  class="email-composer__recipient-tag"
                >
                  <span>{{ recipient.name || recipient.email }}</span>
                  <button
                    type="button"
                    class="email-composer__recipient-remove"
                    @click="removeRecipient('bcc', index)"
                  >
                    <Icon icon="mdi:close" />
                  </button>
                </div>
                <input
                  v-model="bccInput"
                  type="text"
                  class="email-composer__recipient-input"
                  placeholder="Bcc"
                  @keydown.enter.prevent="addRecipient('bcc', bccInput)"
                  @blur="addRecipient('bcc', bccInput)"
                />
              </div>
            </div>
          </div>

          <!-- Subject -->
          <div class="email-composer__row">
            <div class="email-composer__row-label">Subject</div>
            <div class="email-composer__row-input">
              <Input v-model="formData.subject" placeholder="Write a Subject..." size="sm" />
            </div>
          </div>

          <!-- Attachments -->
          <div
            v-if="formData.attachments && formData.attachments.length > 0"
            class="email-composer__attachments"
          >
            <div
              v-for="(attachment, index) in formData.attachments"
              :key="index"
              class="email-composer__attachment-item"
            >
              <Icon icon="mdi:file-outline" />
              <span class="email-composer__attachment-name">{{
                getAttachmentName(attachment)
              }}</span>
              <span
                v-if="getAttachmentSize(attachment) > 0"
                class="email-composer__attachment-size"
              >
                {{ formatFileSize(getAttachmentSize(attachment)) }}
              </span>
              <button
                type="button"
                class="email-composer__attachment-remove"
                @click="removeAttachment(index)"
              >
                <Icon icon="mdi:close" />
              </button>
            </div>
          </div>

          <div class="email-composer__body">
            <RichTextEditor
              ref="richTextEditor"
              v-model="formData.body"
              height="300px"
              placeholder="Compose email"
              :hide-toolbar-items="['link', 'image', 'video', 'formula', 'clean']"
            />
          </div>

          <!-- Signature Preview (read-only) -->
          <div v-if="defaultSignature && defaultSignature.content" class="email-signature-preview">
            <div class="email-signature-preview__content" v-html="defaultSignature.content"></div>
          </div>

          <!-- Actions -->
          <div class="email-composer__actions">
            <div class="email-composer__actions-left">
              <div class="email-composer__send-wrapper" ref="sendButtonRef">
                <div class="email-composer__split-button">
                  <Button
                    variant="primary"
                    size="xs"
                    icon="mdi:send"
                    :loading="isSending"
                    :disabled="!canSend"
                    @click="handleSend"
                    class="email-composer__send-main-btn"
                  >
                    Send
                  </Button>
                  <button
                    type="button"
                    class="email-composer__send-dropdown-btn"
                    :disabled="!canSend"
                    @click.stop="toggleScheduleMenu"
                    title="Schedule send"
                    :aria-expanded="showScheduleMenu"
                  >
                    <Icon icon="mdi:chevron-down" />
                  </button>
                </div>

                <div
                  v-if="showScheduleMenu && canSend"
                  class="email-composer__schedule-menu"
                  @click.stop
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="mdi:clock-outline"
                    block
                    @click="handleScheduleMenuClick"
                    class="email-composer__schedule-menu-item"
                  >
                    Schedule send
                  </Button>
                </div>
              </div>
              <button
                type="button"
                class="email-composer__action-icon-btn email-composer__action-icon-btn--sm"
                @click="handleAttach"
                title="Attach files"
              >
                <Icon icon="mdi:paperclip" />
              </button>
              <div class="email-composer__popover-wrapper" ref="linkPopoverRef">
                <button
                  type="button"
                  class="email-composer__action-icon-btn email-composer__action-icon-btn--sm"
                  @click="toggleLinkPopover"
                  title="Insert link"
                >
                  <Icon icon="mdi:link" />
                </button>
                <div
                  v-if="showLinkPopover"
                  class="email-composer__popover email-composer__popover--link"
                >
                  <div class="email-composer__popover-content">
                    <div class="email-composer__link-field-wrapper">
                      <Icon icon="mdi:format-align-left" class="email-composer__link-field-icon" />
                      <input
                        v-model="linkText"
                        type="text"
                        class="email-composer__link-input email-composer__link-input--text"
                        placeholder="Text"
                        @keyup.enter="insertLink"
                        @keyup.esc="closeLinkPopover"
                      />
                    </div>
                    <div
                      class="email-composer__link-field-wrapper email-composer__link-field-wrapper--url"
                    >
                      <Icon icon="mdi:link" class="email-composer__link-field-icon" />
                      <input
                        v-model="linkUrl"
                        type="text"
                        class="email-composer__link-input email-composer__link-input--url"
                        placeholder="Type or paste a link"
                        @keyup.enter="insertLink"
                        @keyup.esc="closeLinkPopover"
                        ref="linkInputRef"
                      />
                      <button
                        type="button"
                        class="email-composer__link-apply-btn"
                        @click="insertLink"
                        :disabled="!linkUrl || linkUrl.trim() === ''"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="button"
                class="email-composer__action-icon-btn email-composer__action-icon-btn--sm"
                @click="toggleEmojiPopover"
                title="Insert emoji"
              >
                <Icon icon="mdi:emoticon-outline" />
              </button>
              <button
                type="button"
                class="email-composer__action-icon-btn"
                @click="handleSaveDraft"
                :disabled="isSavingDraft"
                title="Save draft"
              >
                <Icon icon="mdi:content-save-outline" />
              </button>
              <button
                type="button"
                class="email-composer__action-icon-btn"
                @click="handleDeleteDraft"
                title="Delete draft"
              >
                <Icon icon="mdi:delete-outline" />
              </button>
            </div>
            <IcpPicker
              :show="showIcpPicker"
              :mode="icpPickerMode"
              :icps="icps"
              class="email-composer__icp-picker-positioned"
              @select="handleIcpSelect"
              @dismiss="dismissIcpPicker"
            />
            <div class="email-composer__actions-right">
              <div class="email-composer__ai-buttons" v-if="showAiButtons">
                <button
                  ref="aiComposeButtonRef"
                  type="button"
                  class="email-composer__ai-btn email-composer__ai-btn--compose"
                  :disabled="aiButtonsDisabled || isGeneratingAI"
                  :title="isGeneratingAI ? 'Generating...' : 'AI Compose'"
                  @click.stop="handleAiCompose"
                >
                  <template v-if="isGeneratingAI && generatingAction === 'compose'">
                    <span class="email-composer__ai-spinner" />
                    <span class="email-composer__ai-btn-text">Generating...</span>
                  </template>
                  <template v-else>
                    <Icon icon="mdi:sparkles" />
                    <span class="email-composer__ai-btn-text">AI Compose</span>
                  </template>
                </button>
                <button
                  v-if="isThread"
                  type="button"
                  class="email-composer__ai-btn email-composer__ai-btn--reply"
                  :disabled="aiButtonsDisabled || isGeneratingAI"
                  :title="isGeneratingAI ? 'Generating...' : 'AI Reply'"
                  @click.stop="handleAiReply"
                >
                  <template v-if="isGeneratingAI && generatingAction === 'reply'">
                    <span class="email-composer__ai-spinner" />
                    <span class="email-composer__ai-btn-text">Generating...</span>
                  </template>
                  <template v-else>
                    <Icon icon="mdi:sparkles" />
                    <span class="email-composer__ai-btn-text">AI Reply</span>
                  </template>
                </button>
                <button
                  v-if="isThread"
                  type="button"
                  class="email-composer__ai-btn email-composer__ai-btn--followup"
                  :disabled="aiButtonsDisabled || isGeneratingAI"
                  :title="isGeneratingAI ? 'Generating...' : 'AI Follow-up'"
                  @click.stop="handleAiFollowup"
                >
                  <template v-if="isGeneratingAI && generatingAction === 'followup'">
                    <span class="email-composer__ai-spinner" />
                    <span class="email-composer__ai-btn-text">Generating...</span>
                  </template>
                  <template v-else>
                    <Icon icon="mdi:sparkles" />
                    <span class="email-composer__ai-btn-text">AI Follow-up</span>
                  </template>
                </button>
                <button
                  ref="aiRefineButtonRef"
                  type="button"
                  class="email-composer__ai-btn email-composer__ai-btn--custom"
                  :disabled="aiButtonsDisabled || isGeneratingAI || isGeneratingAiCustom"
                  :title="isGeneratingAiCustom ? 'Generating...' : 'AI Refine'"
                  @click.stop="toggleAiRefinePanel"
                >
                  <Icon icon="mdi:sparkles" />
                  <span class="email-composer__ai-btn-text">AI Refine</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div
        v-if="showEmojiPopover"
        class="email-composer__emoji-container"
        @click.self="closeEmojiPopover"
      >
        <EmojiPicker @select="handleEmojiSelect" />
      </div>
      <AiRefinePopover
        v-if="showAiButtons"
        :show="showAiPanel"
        :trigger-el="aiRefineButtonRef"
        :disabled="isGeneratingAiCustom"
        @generate="handleAiCustomizationGenerate"
        @close="showAiPanel = false"
      />
      <AiRefinePopover
        v-if="showAiButtons"
        :show="showAiComposePanel"
        :trigger-el="aiComposeButtonRef"
        :disabled="isGeneratingAI && generatingAction === 'compose'"
        title="AI Compose"
        generate-button-label="Generate Message"
        :show-attachments="false"
        @generate="handleAiComposeGenerate"
        @close="showAiComposePanel = false"
      />
    </div>
    <ScheduleSendModal
      :show="showScheduleModal"
      :is-loading="isScheduling"
      @close="showScheduleModal = false"
      @schedule="handleScheduleSend"
    />
    <ActionConfirmationModal
      :show="showDeleteDraftModal"
      title="Delete Draft"
      message="Are you sure you want to delete this draft? This action cannot be undone."
      confirm-text="Delete"
      cancel-text="Cancel"
      icon="mdi:delete-outline"
      @confirm="confirmDeleteDraft"
      @close="showDeleteDraftModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
  type CSSProperties,
} from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import { Icon } from "@iconify/vue";

import Button from "@/components/ui/Button.vue";
import Input from "@/components/ui/Input.vue";
import RichTextEditor from "@/components/ui/RichTextEditor.vue";
import Select from "@/components/ui/Select.vue";
import IcpPicker from "@/components/ui/IcpPicker.vue";
import ActionConfirmationModal from "@/components/modal/ActionConfirmationModal.vue";

import EmojiPicker from "./EmojiPicker.vue";
import ScheduleSendModal from "./ScheduleSendModal.vue";
import AiRefinePopover from "./AiRefinePopover.vue";

import EmailSignatureService from "@/services/email-signature";
import AIGenerationService from "@/services/ai/ai-generation";
import { getEditorHTML, getQuillInstance, isBodyEmpty } from "@/utils/emailComposerHelpers";
import { useToast } from "@/composables/ui/useToast";
import { useDraggable } from "@/composables/ui/useDraggable";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

interface Account {
  id: string;
  email?: string;
  name?: string;
  accountType?: string;
}

interface Recipient {
  email: string;
  name?: string;
}

interface Icp {
  id: string;
  [key: string]: any;
}

interface Props {
  show?: boolean;
  replyTo?: any;
  replyAll?: boolean;
  forwardEmail?: any;
  defaultRecipient?: string;
  accounts?: Account[];
  defaultAccountId?: string;
  initialBody?: string;
  initialSubject?: string;
  showAiButtons?: boolean;
  isThread?: boolean;
  aiButtonsDisabled?: boolean;
  isGeneratingAI?: boolean;
  contactId?: string;
  icpId?: string;
  icps?: Icp[];
  generatingAction?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  replyTo: null,
  replyAll: false,
  forwardEmail: null,
  defaultRecipient: "",
  accounts: () => [],
  defaultAccountId: "",
  initialBody: "",
  initialSubject: "",
  showAiButtons: false,
  isThread: false,
  aiButtonsDisabled: false,
  isGeneratingAI: false,
  contactId: "",
  icpId: "",
  icps: () => [],
  generatingAction: null,
});

const emit = defineEmits<{
  close: [];
  send: [payload: Record<string, any>, done?: (success?: boolean) => void];
  "save-draft": [payload: Record<string, any>];
  "schedule-send": [payload: Record<string, any>];
  "ai-reply": [payload: { icpId?: string }];
  "ai-followup": [payload: { icpId?: string }];
  "ai-compose": [payload: { icpId?: string; customInstructions?: string }];
}>();

const queryClient = useQueryClient();
const toast = useToast();

// State
const formData = reactive<{
  account_id: string;
  to: Recipient[];
  cc: Recipient[];
  bcc: Recipient[];
  subject: string;
  body: string;
  reply_to: string | null;
  attachments: any[];
  tracking_options: { opens: boolean; links: boolean };
}>({
  account_id: "",
  to: [],
  cc: [],
  bcc: [],
  subject: "",
  body: "",
  reply_to: null,
  attachments: [],
  tracking_options: { opens: true, links: true },
});

const recipientInput = ref("");
const ccInput = ref("");
const bccInput = ref("");
const isSending = ref(false);
const isSavingDraft = ref(false);
const showCcBcc = ref(false);
const defaultSignature = ref<any>(null);
const showLinkPopover = ref(false);
const showEmojiPopover = ref(false);
const linkUrl = ref("");
const linkText = ref("");
const showScheduleModal = ref(false);
const showScheduleMenu = ref(false);
const isScheduling = ref(false);
const showDeleteDraftModal = ref(false);
const showAiPanel = ref(false);
const showAiComposePanel = ref(false);
const isGeneratingAiCustom = ref(false);
const showIcpPicker = ref(false);
const icpPickerMode = ref<"reply" | "followup" | "compose" | null>(null);
const pendingComposeIcpId = ref<string | undefined>(undefined);

// Template refs
const richTextEditor = ref<InstanceType<typeof RichTextEditor> | null>(null);
const sendButtonRef = ref<HTMLElement | null>(null);
const linkPopoverRef = ref<HTMLElement | null>(null);
const linkInputRef = ref<HTMLInputElement | null>(null);
const aiRefineButtonRef = ref<HTMLElement | null>(null);
const aiComposeButtonRef = ref<HTMLElement | null>(null);

// Drag handling — uses shared composable
const {
  modalRef,
  isDragging,
  isInitialPosition,
  style: draggableStyle,
  startDrag,
  reset: resetPosition,
} = useDraggable({
  initialWidth: 640,
  initialHeight: 650,
  clampPadding: 16,
  excludedSelectors: [
    ".email-composer__header-btn",
    ".email-composer__content",
    ".email-composer__row",
    ".email-composer__body",
    ".email-composer__actions",
  ],
});

// Computed
const isReply = computed(() => !!props.replyTo);
const isForward = computed(() => !!props.forwardEmail);

const canSend = computed(() => {
  if (!formData.account_id || formData.to.length === 0) return false;
  const editorContent = getEditorHTML(richTextEditor.value, formData.body);
  // If the editor reports empty content, also check formData.body directly —
  // the Quill DOM may not have synced yet after a programmatic body update
  // (e.g. cue card enrichment sets formData.body before the editor re-renders).
  if (!isBodyEmpty(editorContent)) return true;
  return !isBodyEmpty(formData.body);
});

const accountOptions = computed(() =>
  props.accounts.map((account) => {
    const providerLabel = getProviderLabel(account.accountType);
    const displayName = account.email || account.name || "Unknown";
    return {
      label: `${displayName}${providerLabel ? ` (${providerLabel})` : ""}`,
      value: account.id,
    };
  })
);

const modalStyle = computed<CSSProperties>(() => {
  if (isInitialPosition.value) {
    return {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      right: "auto",
      bottom: "auto",
    };
  }
  return draggableStyle.value;
});

// Watchers
watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      nextTick(async () => {
        await loadDefaultSignature();
        initializeForm();
      });
    } else {
      resetForm();
      isScheduling.value = false;
      showScheduleModal.value = false;
      resetPosition();
    }
  },
  { immediate: true }
);

watch(showScheduleModal, (newVal) => {
  if (newVal) {
    showScheduleMenu.value = false;
  } else {
    isScheduling.value = false;
  }
});

watch(
  () => props.defaultAccountId,
  (newVal) => {
    if (newVal && !formData.account_id) {
      formData.account_id = newVal;
    }
  }
);

watch(
  () => formData.account_id,
  (newVal, oldVal) => {
    // reply_to is scoped to the account that owns the parent email. If the user
    // switches the From account, the original reply_to id will not resolve on
    // the new account and Unipile returns "parent mail could not be found".
    if (oldVal && newVal && oldVal !== newVal && formData.reply_to) {
      formData.reply_to = null;
    }
  }
);

// Lifecycle
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});

// Methods
function getProviderLabel(accountType?: string): string {
  if (!accountType) return "";
  const type = accountType.toUpperCase();
  switch (type) {
    case "GOOGLE":
      return "Gmail";
    case "MICROSOFT":
    case "OUTLOOK":
      return "Microsoft Outlook";
    default:
      return "";
  }
}

function initializeForm() {
  formData.account_id =
    props.defaultAccountId || (props.accounts.length > 0 ? props.accounts[0].id : "");
  formData.to = [];
  formData.cc = [];
  formData.bcc = [];
  formData.subject = "";
  formData.body = "";
  formData.reply_to = null;
  formData.attachments = [];
  formData.tracking_options = { opens: true, links: true };
  recipientInput.value = "";
  ccInput.value = "";
  bccInput.value = "";
  showCcBcc.value = false;

  let initialBody = "";

  if (props.initialBody) {
    initialBody = props.initialBody;
    if (props.replyTo) {
      initializeReplyForm();
    } else if (props.forwardEmail) {
      initializeForwardForm();
      if (props.initialSubject) {
        formData.subject = props.initialSubject;
      }
    } else {
      if (props.initialSubject) {
        formData.subject = props.initialSubject;
      }
      if (props.defaultRecipient) {
        addRecipientFromString(props.defaultRecipient, "to");
      }
    }
  } else if (props.replyTo) {
    initializeReplyForm();
    const quotedText =
      props.replyTo.text_body || props.replyTo.body_plain || props.replyTo.body || "";
    initialBody = `<p><br></p><p>--- Original Message ---</p><p>${quotedText.replace(/\n/g, "<br>")}</p>`;
  } else if (props.forwardEmail) {
    initializeForwardForm();
    const forwardedText =
      props.forwardEmail.text_body ||
      props.forwardEmail.body_plain ||
      props.forwardEmail.body ||
      "";
    initialBody = `<p><br></p><p>--- Forwarded Message ---</p><p>${forwardedText.replace(/\n/g, "<br>")}</p>`;
  } else {
    if (props.initialSubject) {
      formData.subject = props.initialSubject;
    }
    if (props.defaultRecipient) {
      addRecipientFromString(props.defaultRecipient, "to");
    }
  }

  formData.body = initialBody;
}

function initializeReplyForm() {
  const userEmails = props.accounts
    .map((acc) => (acc.email || acc.name || "").toLowerCase())
    .filter(Boolean);

  if (props.replyAll) {
    const toRecipients = props.replyTo.to_attendees || props.replyTo.to;
    if (toRecipients) {
      const recipientsArray = Array.isArray(toRecipients) ? toRecipients : [toRecipients];
      formData.to = recipientsArray
        .map((r: any): Recipient | null => {
          const email = r.identifier || r.email;
          if (!email) return null;
          const name = r.display_name || r.name;
          return name ? { email: String(email), name: String(name) } : { email: String(email) };
        })
        .filter((r): r is Recipient => r !== null && !userEmails.includes(r.email.toLowerCase()));
    }
  } else {
    const from = props.replyTo.from_attendee || props.replyTo.from;
    if (from) {
      const senderEmail = from.identifier || from.email;
      const senderName = from.display_name || from.name;

      if (senderEmail && !userEmails.includes(senderEmail.toLowerCase())) {
        formData.to = [
          senderName
            ? { email: String(senderEmail), name: String(senderName) }
            : { email: String(senderEmail) },
        ];
      } else {
        const toRecipients = props.replyTo.to_attendees || props.replyTo.to;
        if (toRecipients) {
          const recipientsArray = Array.isArray(toRecipients) ? toRecipients : [toRecipients];
          const firstOtherRecipient = recipientsArray
            .map((r: any): Recipient | null => {
              const email = r.identifier || r.email;
              if (!email) return null;
              const name = r.display_name || r.name;
              return name ? { email: String(email), name: String(name) } : { email: String(email) };
            })
            .filter(
              (r): r is Recipient => r !== null && !userEmails.includes(r.email.toLowerCase())
            )[0];

          if (firstOtherRecipient) {
            formData.to = [firstOtherRecipient];
          }
        }
      }
    }
  }

  formData.subject = props.replyTo.subject?.startsWith("Re: ")
    ? props.replyTo.subject
    : `Re: ${props.replyTo.subject || ""}`;

  const threadEmails = Array.isArray(props.replyTo.thread_emails)
    ? props.replyTo.thread_emails
    : null;
  const latest =
    threadEmails && threadEmails.length > 0 ? threadEmails[threadEmails.length - 1] : null;
  formData.reply_to = (latest && latest.id) || props.replyTo.id;
}

function initializeForwardForm() {
  formData.to = [];
  formData.subject = props.forwardEmail.subject?.startsWith("Fwd: ")
    ? props.forwardEmail.subject
    : `Fwd: ${props.forwardEmail.subject || ""}`;
}

function resetForm() {
  formData.body = "";
  formData.attachments = [];
}

function addRecipient(field: "to" | "cc" | "bcc", inputValue: string) {
  if (!inputValue || !inputValue.trim()) return;
  addRecipientFromString(inputValue.trim(), field);
  if (field === "to") recipientInput.value = "";
  else if (field === "cc") ccInput.value = "";
  else if (field === "bcc") bccInput.value = "";
}

function addRecipientFromString(input: string, field: "to" | "cc" | "bcc") {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const parts = input.split(/[<>]/);
  let email = "";
  let name = "";

  if (parts.length > 1) {
    name = parts[0].trim();
    email = parts[1].trim();
  } else {
    const trimmed = input.trim();
    if (emailRegex.test(trimmed)) {
      email = trimmed;
    } else {
      const words = trimmed.split(/\s+/);
      email = words[words.length - 1];
      name = words.slice(0, -1).join(" ");
    }
  }

  if (email && emailRegex.test(email)) {
    const recipient: Recipient = { email };
    if (name) recipient.name = name;
    formData[field].push(recipient);
  }
}

function removeRecipient(field: "to" | "cc" | "bcc", index: number) {
  formData[field].splice(index, 1);
}

async function loadDefaultSignature() {
  try {
    const cachedSignature = queryClient.getQueryData(["defaultEmailSignature"]);
    if (cachedSignature) {
      defaultSignature.value = cachedSignature;
      return;
    }

    const response = await EmailSignatureService.getDefaultSignature();
    if (response?.data?.status && response.data.data) {
      const signatureData = response.data.data;
      if (Array.isArray(signatureData) && signatureData.length > 0) {
        defaultSignature.value = signatureData[0];
      } else if (signatureData && typeof signatureData === "object" && signatureData.content) {
        defaultSignature.value = signatureData;
      } else {
        defaultSignature.value = null;
      }
      if (defaultSignature.value) {
        queryClient.setQueryData(["defaultEmailSignature"], defaultSignature.value);
      }
    } else {
      defaultSignature.value = null;
    }
  } catch (error) {
    defaultSignature.value = null;
  }
}

function handleAttach() {
  const input = document.createElement("input");
  input.type = "file";
  input.multiple = true;
  input.accept = "*/*";
  input.onchange = (e: Event) => {
    const files = Array.from((e.target as HTMLInputElement).files || []);
    if (files.length === 0) return;
    if (!validateFiles(files)) {
      input.value = "";
      return;
    }
    formData.attachments.push(...files);
    input.value = "";
  };
  input.click();
}

function validateFiles(files: File[]): boolean {
  for (const file of files) {
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File "${file.name}" is too large. Maximum size is 5MB.`);
      return false;
    }
  }
  return true;
}

function removeAttachment(index: number) {
  formData.attachments.splice(index, 1);
}

function getAttachmentName(attachment: any): string {
  if (attachment instanceof File) return attachment.name;
  if (attachment && typeof attachment === "object" && attachment.name) return attachment.name;
  return String(attachment ?? "");
}

function getAttachmentSize(attachment: any): number {
  if (attachment instanceof File) return attachment.size;
  if (attachment && typeof attachment === "object" && typeof attachment.size === "number") {
    return attachment.size;
  }
  return 0;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}

function toggleLinkPopover() {
  if (showLinkPopover.value) {
    showLinkPopover.value = false;
    return;
  }

  closeAllPopovers();

  if (richTextEditor.value) {
    const quill = getQuillInstance(richTextEditor.value);
    if (quill) {
      const range = quill.getSelection(true);
      if (range && range.length > 0) {
        const selectedText = quill.getText(range.index, range.length);
        linkText.value = selectedText.trim();
      }
    }
  }

  showLinkPopover.value = true;
  nextTick(() => {
    linkInputRef.value?.focus();
  });
}

function closeLinkPopover() {
  showLinkPopover.value = false;
  linkUrl.value = "";
  linkText.value = "";
}

function insertLink() {
  if (!linkUrl.value || linkUrl.value.trim() === "") return;

  if (!richTextEditor.value) {
    closeLinkPopover();
    return;
  }

  try {
    const quill = getQuillInstance(richTextEditor.value);
    if (!quill) {
      closeLinkPopover();
      return;
    }

    const range = quill.getSelection(true);
    if (!range) {
      closeLinkPopover();
      return;
    }

    let url = linkUrl.value.trim();
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "https://" + url;
    }

    const text = linkText.value.trim() || url;

    if (range.length > 0) {
      quill.formatText(range.index, range.length, "link", url);
    } else {
      quill.insertText(range.index, text, "link", url);
      quill.setSelection(range.index + text.length);
    }

    closeLinkPopover();
  } catch (error) {
    closeLinkPopover();
  }
}

function toggleEmojiPopover() {
  if (showEmojiPopover.value) {
    showEmojiPopover.value = false;
  } else {
    closeAllPopovers();
    showEmojiPopover.value = true;
  }
}

function closeEmojiPopover() {
  showEmojiPopover.value = false;
}

function handleEmojiSelect(emoji: any) {
  if (!richTextEditor.value) {
    closeEmojiPopover();
    return;
  }

  try {
    const quill = getQuillInstance(richTextEditor.value);
    if (!quill) {
      closeEmojiPopover();
      return;
    }

    const range = quill.getSelection(true);
    const index = range ? range.index : quill.getLength() - 1;

    const emojiChar = emoji.emoji || emoji.native || emoji;
    quill.insertText(index, emojiChar);
    quill.setSelection(index + emojiChar.length);

    closeEmojiPopover();
  } catch (error) {
    closeEmojiPopover();
  }
}

function closeAllPopovers() {
  showLinkPopover.value = false;
  showEmojiPopover.value = false;
  showScheduleModal.value = false;
}

async function handleSend() {
  if (!canSend.value || isSending.value) return;

  isSending.value = true;
  try {
    await nextTick();
    const payload = getEmailPayload();
    emit("send", payload, (success: boolean = true) => {
      isSending.value = false;
      if (success) {
        formData.attachments = [];
        handleClose();
      }
    });
  } catch (error) {
    isSending.value = false;
  }
}

async function handleSaveDraft() {
  if (!formData.account_id || formData.to.length === 0) return;

  isSavingDraft.value = true;
  try {
    await nextTick();
    const htmlBody = getEditorHTML(richTextEditor.value, formData.body);

    const payload: Record<string, any> = {
      account_id: formData.account_id,
      to: formData.to,
      body: htmlBody,
    };

    if (formData.cc.length > 0) payload.cc = formData.cc;
    if (formData.bcc.length > 0) payload.bcc = formData.bcc;
    if (formData.subject) payload.subject = formData.subject;
    if (formData.reply_to) payload.reply_to = formData.reply_to;
    if (formData.attachments.length > 0) payload.attachments = formData.attachments;

    if (formData.tracking_options) {
      const tracking = formData.tracking_options as any;
      if (tracking.opens || tracking.links || tracking.open_tracking || tracking.click_tracking) {
        payload.tracking_options = {
          opens: tracking.opens ?? tracking.open_tracking ?? false,
          links: tracking.links ?? tracking.click_tracking ?? false,
        };
      }
    }

    emit("save-draft", payload);
  } catch (error) {
    // Error saving draft
  } finally {
    isSavingDraft.value = false;
  }
}

function handleDeleteDraft() {
  showDeleteDraftModal.value = true;
}

function confirmDeleteDraft() {
  showDeleteDraftModal.value = false;
  handleClose();
}

function getEmailPayload(): Record<string, any> {
  const htmlBody = getEditorHTML(richTextEditor.value, formData.body);

  const payload: Record<string, any> = {
    account_id: formData.account_id,
    to: formData.to,
    body: htmlBody,
  };

  if (formData.cc.length > 0) payload.cc = formData.cc;
  if (formData.bcc.length > 0) payload.bcc = formData.bcc;
  if (formData.subject) payload.subject = formData.subject;
  if (formData.reply_to) payload.reply_to = formData.reply_to;
  if (formData.attachments.length > 0) payload.attachments = formData.attachments;

  if (formData.tracking_options) {
    const tracking = formData.tracking_options as any;
    if (tracking.opens || tracking.links || tracking.open_tracking || tracking.click_tracking) {
      payload.tracking_options = {
        opens: tracking.opens ?? tracking.open_tracking ?? false,
        links: tracking.links ?? tracking.click_tracking ?? false,
      };
    }
  }

  return payload;
}

function toggleScheduleMenu() {
  showScheduleMenu.value = !showScheduleMenu.value;
  if (showScheduleMenu.value) {
    closeAllPopovers();
  }
}

function handleScheduleMenuClick() {
  showScheduleMenu.value = false;
  showScheduleModal.value = true;
}

function handleAiReply() {
  if (props.aiButtonsDisabled || props.isGeneratingAI) return;
  if (props.icps && props.icps.length > 1) {
    icpPickerMode.value = "reply";
    showIcpPicker.value = true;
  } else {
    emit("ai-reply", { icpId: props.icps?.[0]?.id });
  }
}

function handleAiFollowup() {
  if (props.aiButtonsDisabled || props.isGeneratingAI) return;
  if (props.icps && props.icps.length > 1) {
    icpPickerMode.value = "followup";
    showIcpPicker.value = true;
  } else {
    emit("ai-followup", { icpId: props.icps?.[0]?.id });
  }
}

function handleAiCompose() {
  if (props.aiButtonsDisabled || props.isGeneratingAI) return;
  if (props.icps && props.icps.length > 1) {
    icpPickerMode.value = "compose";
    showIcpPicker.value = true;
  } else {
    pendingComposeIcpId.value = props.icps?.[0]?.id;
    showAiPanel.value = false;
    showAiComposePanel.value = true;
  }
}

function handleIcpSelect(icpId: string) {
  const mode = icpPickerMode.value;
  showIcpPicker.value = false;
  icpPickerMode.value = null;
  if (mode === "reply") emit("ai-reply", { icpId });
  else if (mode === "followup") emit("ai-followup", { icpId });
  else if (mode === "compose") {
    pendingComposeIcpId.value = icpId;
    showAiPanel.value = false;
    showAiComposePanel.value = true;
  }
}

function toggleAiRefinePanel() {
  showAiComposePanel.value = false;
  showAiPanel.value = !showAiPanel.value;
}

function handleAiComposeGenerate(payload: { customInstructions: string }) {
  emit("ai-compose", {
    icpId: pendingComposeIcpId.value,
    customInstructions: payload.customInstructions,
  });
  showAiComposePanel.value = false;
}

function dismissIcpPicker() {
  showIcpPicker.value = false;
  icpPickerMode.value = null;
}

async function handleAiCustomizationGenerate(payload: {
  customInstructions?: string;
  attachments?: any;
}) {
  if (isGeneratingAiCustom.value) return;
  isGeneratingAiCustom.value = true;
  try {
    const currentDraft = getEditorHTML(richTextEditor.value, formData.body);
    const response = await AIGenerationService.generateRefine({
      contactId: props.contactId || "",
      icpId: props.icpId || undefined,
      channel: "email",
      currentDraft,
      currentSubject: formData.subject || undefined,
      customInstructions: payload.customInstructions || "",
      files: payload.attachments,
    });

    formData.body = response.body;
    if (response.subject) {
      formData.subject = response.subject;
    }

    nextTick(() => {
      if (richTextEditor.value) {
        try {
          const quill = getQuillInstance(richTextEditor.value);
          if (quill && quill.root) {
            quill.root.innerHTML = response.body;
          }
        } catch (err) {
          console.warn("Error updating Quill editor:", err);
        }
      }
    });

    showAiPanel.value = false;
    toast.success("Email content refined");
  } catch (error) {
    console.error("Failed to refine email content:", error);
    toast.error("Failed to refine content");
  } finally {
    isGeneratingAiCustom.value = false;
  }
}

async function handleScheduleSend(scheduleData: { scheduledAt: any }) {
  if (isScheduling.value) return;

  try {
    isScheduling.value = true;
    emit("schedule-send", {
      ...getEmailPayload(),
      scheduledAt: scheduleData.scheduledAt,
    });
  } catch (error) {
    isScheduling.value = false;
  }
}

function handleClickOutside(event: MouseEvent) {
  if (
    showLinkPopover.value &&
    linkPopoverRef.value &&
    !linkPopoverRef.value.contains(event.target as Node)
  ) {
    closeLinkPopover();
  }

  if (
    showScheduleMenu.value &&
    sendButtonRef.value &&
    !sendButtonRef.value.contains(event.target as Node)
  ) {
    showScheduleMenu.value = false;
  }
}

function handleClose() {
  emit("close");
}

defineExpose({ formData, richTextEditor });
</script>

<style scoped>
.email-signature-preview {
  padding: 0 16px 8px;
  user-select: none;
  opacity: 0.85;
  flex-shrink: 0;
  max-height: 160px;
  overflow-y: auto;
}

.email-signature-preview__content {
  font-size: 13px;
  /* color: #6b7280; */
  line-height: 1.5;
}

.email-signature-preview__content :deep(p) {
  margin: 0;
  padding: 0;
  line-height: 1.4;
}

.email-signature-preview__content :deep(br) {
  display: block;
  content: "";
  margin-top: 2px;
}

.email-composer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.email-composer-overlay.dragging-active {
  align-items: flex-start;
  justify-content: flex-start;
}

.email-composer {
  width: 640px;
  max-width: calc(100vw - 32px);
  height: calc(100vh - 80px);
  max-height: calc(100vh - 32px);
  background: #fff;
  position: relative;
  overflow: visible;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  user-select: none;
  transition: box-shadow 0.2s ease;
}

.email-composer.is-dragging {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  cursor: grabbing;
  transition: none;
}

.email-composer__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  font-size: 0.8125rem;
}

.email-composer__header--draggable {
  cursor: move;
  cursor: grab;
  user-select: none;
}

.email-composer__header--draggable:active {
  cursor: grabbing;
}

.email-composer__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.email-composer__header-title {
  font-weight: 500;
  color: #202124;
  font-size: 0.875rem;
}

.email-composer__header-right {
  display: flex;
  gap: 4px;
}

.email-composer__header-btn {
  padding: 4px 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #5f6368;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.email-composer__header-btn:hover {
  background: #f1f3f4;
  color: #202124;
}

.email-composer__content {
  flex: 1;
  overflow: hidden;
  padding: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.email-composer__content form {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.email-composer__row {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  min-height: 48px;
  flex-shrink: 0;
}

.email-composer__row-label {
  width: 80px;
  padding: 12px 16px;
  font-size: 0.8125rem;
  color: #5f6368;
  font-weight: 500;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.email-composer__row-input {
  flex: 1;
  padding: 8px 16px 8px 0;
  display: flex;
  align-items: center;
  background: transparent;
}

.email-composer__required {
  color: #ea4335;
}

.email-composer__recipients {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  width: 100%;
  align-items: center;
  min-height: 32px;
  border: none !important;
  background: transparent !important;
}

.email-composer__recipient-input-wrapper {
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 200px;
  gap: 4px;
  border: none !important;
  background: transparent !important;
}

.email-composer__recipient-tag {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #e8f0fe;
  border: 1px solid #cce0fc;
  border-radius: 16px;
  font-size: 0.8125rem;
  color: #1a73e8;
}

.email-composer__recipient-remove {
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #1a73e8;
  display: flex;
  align-items: center;
  font-size: 16px;
  margin-left: 4px;
}

.email-composer__recipient-remove:hover {
  color: #1557b0;
}

.email-composer__recipient-input {
  flex: 1;
  min-width: 100px;
  border: none !important;
  outline: none !important;
  padding: 4px 0;
  font-size: 0.875rem;
  color: #202124;
  background: transparent !important;
  box-shadow: none !important;
}

.email-composer__recipient-input:focus {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.email-composer__recipient-input:hover {
  border: none !important;
  box-shadow: none !important;
}

.email-composer__recipient-input::placeholder {
  color: #5f6368;
}

.email-composer__cc-toggle {
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: #1a73e8;
  font-size: 0.8125rem;
  cursor: pointer;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
}

.email-composer__cc-toggle:hover {
  background: #f1f3f4;
  border-radius: 4px;
}

.email-composer__attachments {
  padding: 8px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
}

.email-composer__attachment-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 0.8125rem;
}

.email-composer__attachment-name {
  flex: 1;
  color: #202124;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.email-composer__attachment-size {
  color: #6b7280;
  font-size: 0.75rem;
  margin-left: auto;
  flex-shrink: 0;
}

.email-composer__attachment-remove {
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  border-radius: 4px;
  transition: all 0.2s;
  flex-shrink: 0;
}

.email-composer__attachment-remove:hover:not(:disabled) {
  background: #fee2e2;
  color: #dc2626;
}

.email-composer__attachment-remove:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.email-composer__body {
  min-height: 60px;
  flex: 1;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}

.email-composer__body :deep(.rich-text-editor),
.email-composer__body :deep(.rich-text-editor-wrapper) {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.email-composer__body :deep(.ql-toolbar) {
  flex-shrink: 0;
}

.email-composer__body :deep(.ql-container) {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.email-composer__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: #fff;
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-top: 1px solid #e5e7eb;
}

.email-composer__actions-left,
.email-composer__actions-right {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: nowrap;
}

.email-composer__action-icon-btn {
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #5f6368;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.email-composer__action-icon-btn:hover:not(:disabled) {
  background: #f1f3f4;
  color: #202124;
}

.email-composer__action-icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.email-composer__action-icon-btn .iconify[data-icon*="loading"] {
  animation: spin 1s linear infinite;
}

.email-composer__action-icon-btn--sm {
  padding: 6px;
  font-size: 16px;
}

.email-composer__send-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.email-composer__ai-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: 0.75rem;
}

.email-composer__ai-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  padding: 4px 8px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.7rem;
  flex-shrink: 0;
  white-space: nowrap;
}

.email-composer__ai-btn-text {
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1;
}

.email-composer__ai-btn--reply {
  background: rgba(124, 58, 237, 0.1);
  color: #7c3aed;
}

.email-composer__ai-btn--reply:hover:not(:disabled) {
  background: rgba(124, 58, 237, 0.2);
  color: #6d28d9;
}

.email-composer__ai-btn--followup {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.email-composer__ai-btn--followup:hover:not(:disabled) {
  background: rgba(245, 158, 11, 0.2);
  color: #d97706;
}

.email-composer__ai-btn--compose {
  background: rgba(8, 145, 178, 0.1);
  color: #0891b2;
}

.email-composer__ai-btn--compose:hover:not(:disabled) {
  background: rgba(8, 145, 178, 0.2);
  color: #0e7490;
}

.email-composer__ai-btn--custom {
  background: rgba(107, 114, 128, 0.12);
  color: #4b5563;
}

.email-composer__ai-btn--custom:hover:not(:disabled) {
  background: rgba(107, 114, 128, 0.2);
  color: #374151;
}

.email-composer__ai-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.email-composer__split-button {
  display: inline-flex;
  align-items: stretch;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
}

:deep(.email-composer__send-main-btn) {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  padding: 4px 10px !important;
  margin: 0 !important;
  height: 30px !important;
  font-size: 0.75rem !important;
  min-height: 30px !important;
}

.email-composer__send-dropdown-btn {
  width: 28px;
  height: 100%;
  min-height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #3e3efe;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.2s;
  padding: 0;
  margin: 0;
  flex-shrink: 0;
}

.email-composer__send-dropdown-btn:hover {
  background: #3230d6;
  color: #ffffff;
}

.email-composer__send-dropdown-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.email-composer__schedule-menu {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  z-index: 10001;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  overflow: hidden;
}

.email-composer__schedule-menu-item {
  width: 100%;
  justify-content: flex-start;
  text-align: left;
  padding: 8px 12px;
  gap: 4px;
}

.email-composer__schedule-menu-item:hover {
  background: #f1f3f4;
}

.email-composer__schedule-menu-item :deep(.ui-button__icon) {
  margin-right: 0;
  font-size: 16px;
}

.email-composer__popover-wrapper {
  position: relative;
  z-index: 1;
}

.email-composer__popover {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  z-index: 10000;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.email-composer__popover--link {
  width: 240px;
  min-width: 240px;
  max-width: 240px;
}

.email-composer__emoji-container {
  position: absolute;
  bottom: 60px;
  left: 16px;
  z-index: 10002;
  pointer-events: auto;
}

.email-composer__popover-content {
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.email-composer__link-field-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
}

.email-composer__link-field-wrapper--url {
  gap: 6px;
  position: relative;
}

.email-composer__link-field-icon {
  font-size: 16px;
  color: #6b7280;
  flex-shrink: 0;
}

.email-composer__link-input {
  flex: 1;
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 2px !important;
  font-size: 12px;
  outline: none;
  line-height: 1.3;
  transition: border-color 0.2s;
  min-width: 0;
  box-sizing: border-box;
  min-height: 0;
  max-height: 28px;
}

.email-composer__link-input--text:focus {
  border-color: #3e3efe;
}

.email-composer__link-input--url {
  padding-right: 50px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.email-composer__link-input--url:focus {
  border-color: #3e3efe;
}

.email-composer__link-apply-btn {
  position: absolute;
  right: 2px;
  top: 2px;
  bottom: 2px;
  padding: 0 8px;
  border: none;
  border-radius: 2px !important;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  line-height: 1;
  background: #3e3efe;
  color: #ffffff;
  display: flex;
  align-items: center;
  white-space: nowrap;
  min-height: 0;
  max-height: 24px;
}

.email-composer__link-apply-btn:hover:not(:disabled) {
  background: #2e2ed8;
}

.email-composer__link-apply-btn:disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}

:deep(.ui-input) {
  box-shadow: none !important;
  border: none !important;
}

:deep(.ui-input__container) {
  box-shadow: none !important;
  border: none !important;
  background: transparent !important;
}

:deep(.ui-input__field) {
  border: none !important;
  padding: 8px 0 !important;
  font-size: 0.875rem !important;
  box-shadow: none !important;
  background: transparent !important;
  height: auto !important;
}

:deep(.ui-input__field:focus) {
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
}

:deep(.ui-input__field:hover) {
  box-shadow: none !important;
  border: none !important;
}

:deep(.ui-input__field:hover:not(:disabled)) {
  box-shadow: none !important;
  border: none !important;
}

:deep(.ui-select__field) {
  border: none;
  padding: 8px 0;
  font-size: 0.875rem;
}

:deep(.ui-select__field:focus) {
  box-shadow: none;
  border: none;
}

:deep(.rich-text-editor) {
  border: none;
}

:deep(.ql-toolbar) {
  border: none;
  border-bottom: 1px solid #e5e7eb;
  padding: 4px 8px;
}

:deep(.ql-container) {
  border: none;
  font-size: 0.8125rem;
}

:deep(.ql-editor) {
  font-size: 0.8125rem;
  padding: 12px 16px;
  /* min-height: 160px !important; */
  color: #202124;
}

/* ICP Picker positioning */
.email-composer__icp-picker-positioned {
  position: absolute;
  bottom: 46px;
  right: 8px;
  left: auto;
  min-width: 240px;
  z-index: 10;
}

.email-composer__ai-spinner {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  border-top-color: #f97316;
  animation: email-composer-ai-spin 0.7s linear infinite;
  display: inline-block;
  flex-shrink: 0;
}

@keyframes email-composer-ai-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
